import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  ChangeDetectionStrategy,
  Component,
  Directive,
  ElementRef,
  HostListener,
  OnDestroy,
  inject
} from '@angular/core';

@Component({
  selector: 'sim-ellipsis-tooltip-bubble',
  standalone: true,
  template: `<div class="ui-tooltip-wrapper">{{ text }}</div>`,
  styleUrl: './ellipsis-tooltip-bubble.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EllipsisTooltipBubbleComponent {
  text = '';
}

@Directive({
  selector: '[simEllipsisTooltip]',
  standalone: true
})
export class EllipsisTooltipDirective implements OnDestroy {
  private readonly _el = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly _overlay = inject(Overlay);
  private _overlayRef?: OverlayRef;

  @HostListener('mouseenter')
  protected onMouseEnter(): void {
    const el = this._el.nativeElement;
    const isTruncated = el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight;
    const text = (el.textContent ?? '').trim();
    if (!isTruncated || !text) return;
    this._open(text);
  }

  @HostListener('mouseleave')
  protected onMouseLeave(): void {
    this._close();
  }

  ngOnDestroy(): void {
    this._close();
  }

  private _open(text: string): void {
    if (this._overlayRef) return;

    this._overlayRef = this._overlay.create({
      scrollStrategy: this._overlay.scrollStrategies.close(),
      positionStrategy: this._overlay
        .position()
        .flexibleConnectedTo(this._el)
        .withPositions([
          { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
          { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 }
        ])
    });

    const ref = this._overlayRef.attach(new ComponentPortal(EllipsisTooltipBubbleComponent));
    ref.instance.text = text;
    ref.changeDetectorRef.markForCheck();
  }

  private _close(): void {
    this._overlayRef?.dispose();
    this._overlayRef = undefined;
  }
}
